'use client'

import { useMemo, useState } from 'react'
import {
  DEFAULT_SELECTED_MODULES,
  PRICING_MODULES,
  formatUsd,
  sumModules,
  type PricingModuleId,
} from '@/lib/pricingCatalog'
import { RESTRO_REGISTER_URL } from '@/config/portal'
import '@/styles/pricing-calculator.css'

function withDependencies(selected: Set<PricingModuleId>): Set<PricingModuleId> {
  const next = new Set(selected)
  let changed = true
  while (changed) {
    changed = false
    for (const mod of PRICING_MODULES) {
      if (!next.has(mod.id) || !mod.needs?.length) continue
      for (const need of mod.needs) {
        if (!next.has(need)) {
          next.add(need)
          changed = true
        }
      }
    }
  }
  // POS always on
  next.add('pos')
  return next
}

export default function PricingCalculator() {
  const [selected, setSelected] = useState<Set<PricingModuleId>>(
    () => withDependencies(new Set(DEFAULT_SELECTED_MODULES)),
  )

  const total = useMemo(() => sumModules(selected), [selected])

  const toggle = (id: PricingModuleId) => {
    const mod = PRICING_MODULES.find((m) => m.id === id)
    if (!mod || mod.locked) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
        // Also drop modules that depend on this one
        for (const m of PRICING_MODULES) {
          if (m.needs?.includes(id)) next.delete(m.id)
        }
      } else {
        next.add(id)
      }
      return withDependencies(next)
    })
  }

  const lines = PRICING_MODULES.filter((m) => selected.has(m.id))

  return (
    <section id="pricing-calculator" className="pcalc" aria-labelledby="pcalc-title">
      <div className="pcalc__head">
        <h2 id="pcalc-title" className="pcalc__title">
          Build your monthly bill
        </h2>
        <p className="pcalc__sub">
          Toggle modules. Dependencies unlock automatically. POS Core stays on. One total covers{' '}
          <strong>all branches</strong>.
        </p>
      </div>

      <div className="pcalc__grid">
        <ul className="pcalc__modules" aria-label="Modules">
          {PRICING_MODULES.map((mod) => {
            const on = selected.has(mod.id)
            return (
              <li key={mod.id}>
                <button
                  type="button"
                  className={
                    'pcalc__mod' +
                    (on ? ' pcalc__mod--on' : '') +
                    (mod.noTrial ? ' pcalc__mod--ai' : '') +
                    (mod.locked ? ' pcalc__mod--locked' : '')
                  }
                  onClick={() => toggle(mod.id)}
                  aria-pressed={on}
                  disabled={mod.locked}
                >
                  <span className="pcalc__check" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                  <span className="pcalc__mod-body">
                    <span className="pcalc__mod-name">
                      {mod.name}
                      {mod.noTrial ? <em> · No trial</em> : null}
                    </span>
                    {mod.note ? <span className="pcalc__mod-note">{mod.note}</span> : null}
                  </span>
                  <span className="pcalc__mod-price">
                    {formatUsd(mod.priceUsd)}
                    <small>/mo</small>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>

        <aside className="pcalc__stack">
          <h3 className="pcalc__stack-title">Your stack</h3>
          <p className="pcalc__branches">All branches included</p>
          <ul className="pcalc__lines">
            {lines.map((m) => (
              <li key={m.id}>
                <span>{m.name}</span>
                <span>{formatUsd(m.priceUsd)}</span>
              </li>
            ))}
          </ul>
          <div className="pcalc__total">
            <span>Total / month</span>
            <strong>{formatUsd(total)}</strong>
          </div>
          <p className="pcalc__hint">
            Most modules include a 30-day trial. AI Receptionist is paid from day one (no trial).
          </p>
          <a className="pcalc__cta" href={RESTRO_REGISTER_URL}>
            Start free trial
          </a>
        </aside>
      </div>
    </section>
  )
}
